/* eslint-disable global-require */
import ReactDOM from 'react-dom';
import { HistoryAdapter, browserHistory, RouterContext } from 'mobx-state-router';
import type h from 'history';
import 'react-virtualized/styles.css';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ReactGA from 'react-ga4';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

import { RootStore, storeContext } from 'logic';
import { ProjectTabProvider } from 'ui/ProjectTabContext';
import { App } from './App';

import 'index.css';

const rootStoreInstance = new RootStore();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).rootStore = rootStoreInstance;
rootStoreInstance.init();
const historyAdapter = new HistoryAdapter(
  rootStoreInstance.routerStore,
  browserHistory as h.History,
);
historyAdapter.observeRouterStateChanges();

ReactGA.initialize('G-ZGWW2X6KLP');
ReactGA.send('pageview');

historyAdapter.history.listen(({ pathname }) => {
  ReactGA.send({
    hitType: 'pageview',
    page: pathname,
  });
});

Sentry.init({
  enabled: process.env.NODE_ENV !== 'development',
  dsn: 'https://c9d96b33aefc42f09f10c661f7a0b34d@o1219955.ingest.sentry.io/6362564',
  integrations: [new BrowserTracing()],

  // We recommend adjusting this value in production, or using tracesSampler
  // for finer control
  tracesSampleRate: 0.5,
  environment: process.env.NODE_ENV,
  release: __COMMIT_HASH__,

  // Dexie/dexie-observable can throw when IndexedDB is closed, browser is shutting down,
  // observable internals see null, or "Out of sync" when observable state and DB diverge.
  // We harden UiSourceFieldsStore; ignore so Dexie noise doesn't flood Sentry.
  // Firebase Auth throws when a second sign-in popup is opened while one is already in progress.
  ignoreErrors: [
    'DatabaseClosedError',
    /Database has been closed/,
    /Browser is shutting down/,
    /This operation has been cancelled due to another conflicting popup being opened/,
  ],

  beforeSend(event, _hint) {
    const msg = event.exception?.values?.[0]?.value;
    const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];

    const stackLooksLikeDexie = (f: { filename?: string; module?: string; function?: string }) => {
      const s = (f.filename ?? f.module ?? f.function ?? '').toLowerCase();
      return (
        s.includes('dexie') ||
        s.includes('idbtransaction') ||
        s.includes('endmicrotickscope') ||
        s.includes('getbykeypath') ||
        s.includes('hasown') ||
        s.includes('calllistener')
      );
    };

    // Dexie/MobX observable internals can throw this when operating on null/undefined (e.g. hasOwnProperty).
    // Minified stacks may only show assets/index + getByKeyPath/callListener/endMicroTickScope.
    if (msg === 'Cannot convert undefined or null to object') {
      if (frames.some(stackLooksLikeDexie)) return null;
    }
    // Dexie/dexie-observable "Out of sync" when observable layer and IndexedDB diverge (e.g. transaction timing).
    if (msg === 'Out of sync') {
      if (frames.some(stackLooksLikeDexie)) return null;
    }
    // Firebase callable "internal" = backend/transient error; don't flood Sentry.
    if (msg === 'internal' || msg === 'Error: internal') {
      const fromFirebaseFunctions = frames.some((f) =>
        (f.filename ?? f.module ?? '').toLowerCase().includes('firebase/functions'),
      );
      if (fromFirebaseFunctions) return null;
    }
    return event;
  },
});

// Handle known-benign unhandled rejections so they don't surface to console/Sentry.
// - Dexie: DB closed, browser shutting down, "Out of sync" (observable/IndexedDB divergence).
// - Firebase Auth: second sign-in popup cancelled (user double-click or rapid click).
// - Firebase callable "internal": backend/transient error; call sites should show UI feedback.
window.addEventListener('unhandledrejection', (event) => {
  const r = event.reason;
  const msg = r?.message;
  if (
    r?.name === 'DatabaseClosedError' ||
    msg === 'Database has been closed' ||
    msg === 'Browser is shutting down' ||
    msg === 'Out of sync' ||
    msg === 'This operation has been cancelled due to another conflicting popup being opened'
  ) {
    event.preventDefault();
    return;
  }
  // Firebase HttpsError "internal" (callable failed with server/transient error)
  if (msg === 'internal' && r?.code === 'internal') {
    event.preventDefault();
  }
});

const renderApp = (AppComponent: typeof App) => {
  ReactDOM.render(
    <RouterContext.Provider value={rootStoreInstance.routerStore}>
      <storeContext.Provider value={rootStoreInstance}>
        <DndProvider backend={HTML5Backend}>
          <ProjectTabProvider>
            <AppComponent />
          </ProjectTabProvider>
        </DndProvider>
      </storeContext.Provider>
    </RouterContext.Provider>,
    document.getElementById('root'),
  );
};

renderApp(App);
